// Orphan Quotes — bulk manage tool for quotes without a Customer attached.
// - Table with checkboxes, select-all
// - Row actions: Open in Builder (to attach a customer manually)
// - Bulk: Delete selected · Delete ALL orphans (with confirmation)
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle, ArrowLeft, FileText, Trash2, ExternalLink, Loader2,
  RefreshCw, ShieldAlert,
} from "lucide-react";
import Layout from "../../components/Layout";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const GBP = (n) => (n === null || n === undefined || Number.isNaN(Number(n)))
  ? "—"
  : "£" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const relTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(+d)) return "—";
  const days = Math.round((Date.now() - +d) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1d";
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
};

export default function OrphanQuotes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});   // { id: true }
  const [busy, setBusy] = useState(false);
  const [confirmingAll, setConfirmingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/deals/orphan-quotes?limit=500`, { withCredentials: true });
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load orphans");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const allChecked = items.length > 0 && selectedIds.length === items.length;
  const someChecked = selectedIds.length > 0 && !allChecked;

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(items.map((it) => [it.id, true])));
  };

  const del = async (opts) => {
    setBusy(true);
    try {
      const body = opts.all ? { all: true } : { ids: selectedIds };
      const { data } = await axios.post(`${API}/deals/orphan-quotes/delete`, body, { withCredentials: true });
      toast.success(
        data.requested && data.requested !== data.deleted
          ? `Deleted ${data.deleted} of ${data.requested} — ${data.requested - data.deleted} had a customer attached (skipped for safety).`
          : `Deleted ${data.deleted} quote${data.deleted === 1 ? "" : "s"}`
      );
      setSelected({});
      setConfirmingAll(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-5 max-w-6xl mx-auto" data-testid="orphan-quotes-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link to="/deals" className="text-[11px] font-mono uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Deal Pipeline
            </Link>
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono mt-2 mb-1 flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> Orphan Quotes
            </div>
            <h1 className="text-3xl font-display font-bold tracking-tight">Quotes without a customer</h1>
            <p className="text-slate-400 text-sm mt-1 max-w-2xl">
              These <span className="text-amber-300 font-semibold">{count}</span> quote{count === 1 ? "" : "s"}
              have no customer attached, so they don&rsquo;t appear on any deal or Person 360. Delete the junk;
              open the ones that matter and pick a customer inside the builder.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              data-testid="orphan-refresh"
              className="w-9 h-9 grid place-items-center rounded-lg border border-white/10 bg-white/[0.03] text-slate-300 hover:text-white hover:bg-white/[0.06] disabled:opacity-50"
              aria-label="Refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Bulk toolbar */}
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 flex items-center gap-3 flex-wrap" data-testid="orphan-toolbar">
          <div className="text-[11px] text-slate-400 font-mono">
            {selectedIds.length > 0
              ? <><span className="text-amber-300 font-semibold">{selectedIds.length}</span> selected</>
              : "Select rows to delete in bulk"}
          </div>
          <button
            disabled={selectedIds.length === 0 || busy}
            onClick={() => del({ ids: selectedIds })}
            data-testid="orphan-delete-selected"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] border border-rose-500/40 bg-rose-500/[0.08] text-rose-200 hover:bg-rose-500/[0.16] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete selected ({selectedIds.length})
          </button>
          <div className="ml-auto flex items-center gap-2">
            {!confirmingAll ? (
              <button
                disabled={count === 0 || busy}
                onClick={() => setConfirmingAll(true)}
                data-testid="orphan-delete-all-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] border border-white/15 text-slate-300 hover:text-white hover:bg-white/[0.06] disabled:opacity-40"
              >
                <ShieldAlert className="w-3.5 h-3.5" /> Delete ALL {count} orphans…
              </button>
            ) : (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-rose-500/50 bg-rose-500/[0.12]">
                <ShieldAlert className="w-4 h-4 text-rose-300" />
                <span className="text-[11.5px] text-rose-100 font-mono">Delete all {count} orphans? Cannot be undone.</span>
                <button
                  onClick={() => del({ all: true })}
                  disabled={busy}
                  data-testid="orphan-delete-all-confirm"
                  className="px-2.5 py-1 rounded-md text-[11.5px] font-mono uppercase tracking-widest bg-rose-500 text-white border border-rose-400 hover:bg-rose-600 shadow-lg shadow-rose-500/20"
                >
                  {busy ? "Deleting…" : "Yes, delete all"}
                </button>
                <button
                  onClick={() => setConfirmingAll(false)}
                  className="px-2 py-0.5 rounded text-[11px] border border-white/15 text-slate-300 hover:text-white"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orphan-table">
              <thead className="bg-amber-500/10 border-b border-amber-500/20">
                <tr className="text-[10px] font-mono uppercase tracking-widest text-amber-200">
                  <th className="py-2 pl-3 pr-2 w-8">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => { if (el) el.indeterminate = someChecked; }}
                      onChange={toggleAll}
                      data-testid="orphan-select-all"
                      className="accent-amber-400"
                    />
                  </th>
                  <th className="py-2 pr-3 text-left">Ref</th>
                  <th className="py-2 pr-3 text-left">Status</th>
                  <th className="py-2 pr-3 text-right">Items</th>
                  <th className="py-2 pr-3 text-right">Total</th>
                  <th className="py-2 pr-3 text-left">Created by</th>
                  <th className="py-2 pr-3 text-left">Age</th>
                  <th className="py-2 pr-3 text-right w-32">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && items.length === 0 && (
                  <tr><td colSpan={8} className="py-16 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={8} className="py-16 text-center text-slate-500">
                    <div className="text-lg mb-1">All clean 🎉</div>
                    <div className="text-[12px]">Every quote has a customer attached.</div>
                  </td></tr>
                )}
                {items.map((q) => (
                  <tr key={q.id} className="border-b border-white/[0.04] hover:bg-white/[0.03]" data-testid={`orphan-row-${q.id}`}>
                    <td className="py-2.5 pl-3 pr-2">
                      <input
                        type="checkbox"
                        checked={!!selected[q.id]}
                        onChange={() => toggle(q.id)}
                        data-testid={`orphan-check-${q.id}`}
                        className="accent-amber-400"
                      />
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-mono text-amber-200">
                        <FileText className="w-3 h-3" /> {q.quote_ref || q.id.slice(-6)}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-[11px] font-mono uppercase tracking-widest text-slate-300">
                      {q.status}
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-[12px] text-slate-400">{q.line_items}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-[12.5px] text-white">{GBP(q.total)}</td>
                    <td className="py-2.5 pr-3 text-[12px] text-slate-400">{q.created_by || <span className="text-slate-600">—</span>}</td>
                    <td className="py-2.5 pr-3 text-[10.5px] text-slate-500 font-mono">{relTime(q.created_at)}</td>
                    <td className="py-2.5 pr-3 text-right">
                      <button
                        onClick={() => navigate(`/quotes/${q.id}/edit`)}
                        data-testid={`orphan-open-${q.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-white/15 text-slate-200 hover:text-white hover:bg-white/[0.06] text-[11px]"
                      >
                        <ExternalLink className="w-3 h-3" /> Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-[10.5px] text-slate-600 font-mono">
          Delete is a hard-delete on <code className="text-slate-500">quotes</code> (guarded so it never
          touches quotes with a Customer.email attached, and never touches seed quotes).
        </div>
      </div>
    </Layout>
  );
}
